import { useState, useEffect } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/cms/services`);
      setServices(response.data.filter(s => s.active));
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) acc[service.category] = [];
    acc[service.category].push(service);
    return acc;
  }, {});

  const bridalServices = (groupedServices.bridal || []).sort((a, b) => a.display_order - b.display_order);
  const eventServices = (groupedServices.event || []).sort((a, b) => a.display_order - b.display_order);
  const productionServices = (groupedServices.production || []).sort((a, b) => a.display_order - b.display_order);
  const additionalServices = (groupedServices.additional || []).sort((a, b) => a.display_order - b.display_order);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="services-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Services & Investment</h1>
          <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
            Luxury hair & makeup artistry designed for your most important moments.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-6">
          {bridalServices.length > 0 && (
            <AccordionItem value="bridal" className="bg-white rounded-lg border-0 shadow-sm overflow-hidden">
              <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-[#F9F8F6] transition-colors">
                <div className="text-left">
                  <h2 className="text-2xl font-bold mb-2">Bridal Services</h2>
                  <p className="text-[#8C8C8C] text-sm">Your wedding day, perfected</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <div className="space-y-6">
                  {bridalServices.map((service, index) => (
                    <div key={service.id} className={index > 0 ? "border-t pt-6" : ""}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-semibold">{service.name}</h3>
                            {service.is_popular && (
                              <span className="text-xs bg-[#D4AF37] text-white px-2 py-1 rounded">⭐ Most Popular</span>
                            )}
                          </div>
                          <p className="text-[#8C8C8C] mb-3">{service.description}</p>
                        </div>
                        {service.price > 0 && (
                          <span className="text-2xl font-bold text-[#D4AF37] ml-4">${service.price.toFixed(0)}</span>
                        )}
                      </div>
                      {service.includes && service.includes.length > 0 && (
                        <ul className="space-y-2 text-[#8C8C8C]">
                          {service.includes.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {eventServices.length > 0 && (
            <AccordionItem value="events" className="bg-white rounded-lg border-0 shadow-sm overflow-hidden">
              <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-[#F9F8F6] transition-colors">
                <div className="text-left">
                  <h2 className="text-2xl font-bold mb-2">Event Services</h2>
                  <p className="text-[#8C8C8C] text-sm">Galas, celebrations, and special occasions</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <div className="space-y-6">
                  {eventServices.map((service, index) => (
                    <div key={service.id} className={index > 0 ? "border-t pt-6" : ""}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-xl font-semibold">{service.name}</h3>
                            {service.is_popular && (
                              <span className="text-xs bg-[#D4AF37] text-white px-2 py-1 rounded">⭐ Most Popular</span>
                            )}
                          </div>
                          <p className="text-[#8C8C8C] mb-3">{service.description}</p>
                        </div>
                        {service.price > 0 && (
                          <span className="text-2xl font-bold text-[#D4AF37] ml-4">${service.price.toFixed(0)}</span>
                        )}
                      </div>
                      {service.includes && service.includes.length > 0 && (
                        <ul className="space-y-2 text-[#8C8C8C]">
                          {service.includes.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {productionServices.length > 0 && (
            <AccordionItem value="production" className="bg-white rounded-lg border-0 shadow-sm overflow-hidden">
              <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-[#F9F8F6] transition-colors">
                <div className="text-left">
                  <h2 className="text-2xl font-bold mb-2">Production Services</h2>
                  <p className="text-[#8C8C8C] text-sm">Film, photo, and commercial projects</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <div className="space-y-6">
                  {productionServices.map((service, index) => (
                    <div key={service.id} className={index > 0 ? "border-t pt-6" : ""}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                          <p className="text-[#8C8C8C]">{service.description}</p>
                        </div>
                        {service.price > 0 && (
                          <span className="text-2xl font-bold text-[#D4AF37] ml-4">${service.price.toFixed(0)}+</span>
                        )}
                      </div>
                      {service.includes && service.includes.length > 0 && (
                        <ul className="space-y-2 text-[#8C8C8C]">
                          {service.includes.map((item, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {additionalServices.length > 0 && (
            <AccordionItem value="additional" className="bg-white rounded-lg border-0 shadow-sm overflow-hidden">
              <AccordionTrigger className="px-8 py-6 hover:no-underline hover:bg-[#F9F8F6] transition-colors">
                <div className="text-left">
                  <h2 className="text-2xl font-bold mb-2">Additional Services & Fees</h2>
                  <p className="text-[#8C8C8C] text-sm">Enhancements and special requirements</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-8 pb-6">
                <div className="space-y-4">
                  {additionalServices.map((service, index) => (
                    <div key={service.id} className={index > 0 ? "border-t pt-4" : ""}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold mb-1">{service.name}</h3>
                          <p className="text-sm text-[#8C8C8C]">{service.description}</p>
                        </div>
                        <span className="text-xl font-bold text-[#D4AF37] ml-4">${service.price.toFixed(0)}+</span>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        <div className="mt-12 bg-[#F2EBE5] p-8 rounded-lg">
          <h3 className="text-xl font-bold mb-3">Destination Services</h3>
          <p className="text-[#8C8C8C] mb-4">
            Travel and destination services available. Pricing quoted after consultation.
          </p>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/inquiry"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            Book Your Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;